// src/context/FirebaseContext.js
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'; // Ensure useCallback and useMemo are imported
import { auth, googleProvider, sendEmailVerification } from '../firebase'; // Assuming these are correctly initialized Firebase instances
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth'; // Ensure onAuthStateChanged is imported
import { CircularProgress } from '@mui/material'; // Assuming MUI is available for consistency

// --- Define Context ---
const FirebaseContext = createContext(null);

// --- Custom Hook ---
export function useFirebase() {
    const context = useContext(FirebaseContext);
    if (context === null) {
        throw new Error('useFirebase must be used within a FirebaseProvider');
    }
    return context;
}

// --- Provider Component ---
export function FirebaseProvider({ children }) {
    // State to hold firebase instances, user, and readiness flags
    const [firebaseState, setFirebaseState] = useState({
        auth: null,
        googleProvider: googleProvider, // Store static instance if needed
        sendEmailVerification: sendEmailVerification, // Store static function if needed
        user: null,
        loadingAuth: true, // <<< Start as TRUE: Initial auth state is unknown
        error: null,
    });

    // --- Auth Actions (Define hooks BEFORE conditional returns) ---
    const signInWithGoogle = useCallback(async () => {
        // Use firebaseState.auth which might be null initially, handle that
        const currentAuth = firebaseState.auth || auth; // Fallback to imported auth if state isn't set yet? Risky. Better to wait.
        if (!currentAuth) {
            console.error("signInWithGoogle: Auth instance not ready in state.");
            throw new Error("Authentication service not ready.");
        }
        try {
            // Use googleProvider from state or direct import
            const currentGoogleProvider = firebaseState.googleProvider || googleProvider;
            const result = await signInWithPopup(currentAuth, currentGoogleProvider);
            const user = result.user;
            // Verification email logic (keep if needed)
            if (user && !user.emailVerified && firebaseState.sendEmailVerification) {
                await firebaseState.sendEmailVerification(user);
                console.log('Verification email sent to:', user.email);
            }
            console.log("signInWithGoogle successful.");
        } catch (err) {
            console.error('Login failed:', err);
            if (err.code === 'auth/popup-closed-by-user') {
                throw new Error("Login cancelled.");
            }
            throw new Error(`Login failed: ${err.message}`);
        }
        // Dependencies: Include everything used from state or props
    }, [firebaseState.auth, firebaseState.googleProvider, firebaseState.sendEmailVerification]);

    const logout = useCallback(async () => {
        const currentAuth = firebaseState.auth || auth; // Fallback, but ideally wait for state
        if (!currentAuth) {
            console.error("logout: Auth instance not ready in state.");
            throw new Error("Authentication service not ready.");
        }
        try {
            await signOut(currentAuth);
            console.log("logout successful.");
        } catch (err) {
            console.error('Logout failed:', err);
            throw new Error(`Logout failed: ${err.message}`);
        }
    }, [firebaseState.auth]); // Dependency

    // --- Provide Context Value (Define hooks BEFORE conditional returns) ---
    // Memoize based on relevant parts of firebaseState and actions
    const contextValue = useMemo(() => ({
        auth: firebaseState.auth,
        user: firebaseState.user,
        loadingAuth: firebaseState.loadingAuth, // Provide the loading flag
        googleProvider: firebaseState.googleProvider,
        sendEmailVerification: firebaseState.sendEmailVerification,
        signInWithGoogle,
        logout,
        // Add error state if components need to react to it directly
        error: firebaseState.error
    }), [firebaseState.auth, firebaseState.user, firebaseState.loadingAuth, firebaseState.googleProvider, firebaseState.sendEmailVerification, firebaseState.error, signInWithGoogle, logout]);


    // --- Effect to Initialize and Listen (Hook called BEFORE conditional returns) ---
    useEffect(() => {
        console.log("[FirebaseProvider] Attaching onAuthStateChanged listener...");

        if (!auth) { // Check the imported auth instance directly here
            console.error("[FirebaseProvider] Firebase Auth instance (imported) is not available!");
            setFirebaseState(prev => ({
                ...prev,
                loadingAuth: false,
                error: 'Firebase Auth failed to initialize.'
            }));
            return;
        }

        // Set auth instance in state once - this will trigger a re-render
        // where loadingAuth is still true, but auth is now set
        setFirebaseState(prev => ({ ...prev, auth: auth }));

        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            console.log("[FirebaseProvider] onAuthStateChanged triggered. User:", currentUser?.uid || null);
            // Update user AND set loadingAuth to false
            setFirebaseState(prev => ({
                ...prev,
                user: currentUser ? { ...currentUser } : null,
                loadingAuth: false, // <<< Set loading to FALSE here
                error: null // Clear error on successful check
            }));
        }, (listenerError) => {
            console.error("[FirebaseProvider] Error in onAuthStateChanged listener:", listenerError);
            setFirebaseState(prev => ({
                ...prev,
                user: null,
                loadingAuth: false, // Stop loading even on error
                error: "Error checking authentication status."
            }));
        });

        return () => {
            console.log("[FirebaseProvider] Cleaning up auth listener.");
            unsubscribe();
        };
        // Run only once on mount
    }, []); // Empty dependency array


    // --- Loading and Error States (Checks AFTER all hooks are defined/called) ---
    if (firebaseState.error) {
        return (
            <div className="flex flex-col min-h-screen bg-gray-900 text-white justify-center items-center p-4 text-center">
                <p className="text-red-400 text-xl font-semibold">Initialization Error</p>
                <p className="text-red-300 mt-2">{firebaseState.error}</p>
            </div>
        );
    }

// Show loading indicator while initial auth check is running
if (firebaseState.loadingAuth) {
    return (
        <div className="flex flex-col min-h-screen bg-gray-900 text-white justify-center items-center">
            <div className="wheel-and-hamster" aria-label="Vibrant purple and blue hamster running in a metal wheel" role="img">
                <div className="wheel" />
                <div className="hamster">
                    <div className="hamster__body">
                        <div className="hamster__head">
                            <div className="hamster__ear" />
                            <div className="hamster__eye" />
                            <div className="hamster__nose" />
                        </div>
                        <div className="hamster__limb hamster__limb--fr" />
                        <div className="hamster__limb hamster__limb--fl" />
                        <div className="hamster__limb hamster__limb--br" />
                        <div className="hamster__limb hamster__limb--bl" />
                        <div className="hamster__tail" />
                    </div>
                </div>
                <div className="spoke" />
            </div>
            <p className="mt-4">Initializing Authentication...</p>
            <style jsx>{`
                .wheel-and-hamster {
                    --dur: 1s;
                    position: relative;
                    width: 12em;
                    height: 12em;
                    font-size: 14px;
                }

                .wheel,
                .hamster,
                .hamster div,
                .spoke {
                    position: absolute;
                }

                .wheel,
                .spoke {
                    border-radius: 50%;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                }

                .wheel {
                    background: radial-gradient(100% 100% at center, hsla(0,0%,60%,0) 47.8%, hsl(270,80%,75%) 48%);
                    z-index: 2;
                }

                .hamster {
                    animation: hamster var(--dur) ease-in-out infinite;
                    top: 50%;
                    left: calc(50% - 3.5em);
                    width: 7em;
                    height: 3.75em;
                    transform: rotate(4deg) translate(-0.8em, 1.85em);
                    transform-origin: 50% 0;
                    z-index: 1;
                }

                .hamster__head {
                    animation: hamsterHead var(--dur) ease-in-out infinite;
                    background: hsl(270,90%,65%);
                    border-radius: 70% 30% 0 100% / 40% 25% 25% 60%;
                    box-shadow: 0 -0.25em 0 hsl(270,90%,80%) inset,
                        0.75em -1.55em 0 hsl(270,90%,90%) inset;
                    top: 0;
                    left: -2em;
                    width: 2.75em;
                    height: 2.5em;
                    transform-origin: 100% 50%;
                }

                .hamster__ear {
                    animation: hamsterEar var(--dur) ease-in-out infinite;
                    background: hsl(250,80%,75%);
                    border-radius: 50%;
                    box-shadow: -0.25em 0 hsl(270,90%,65%) inset;
                    top: -0.25em;
                    right: -0.25em;
                    width: 0.75em;
                    height: 0.75em;
                    transform-origin: 50% 75%;
                }

                .hamster__eye {
                    animation: hamsterEye var(--dur) linear infinite;
                    background-color: hsl(0,0%,0%);
                    border-radius: 50%;
                    top: 0.375em;
                    left: 1.25em;
                    width: 0.5em;
                    height: 0.5em;
                }

                .hamster__nose {
                    background: hsl(260,80%,70%);
                    border-radius: 35% 65% 85% 15% / 70% 50% 50% 30%;
                    top: 0.75em;
                    left: 0;
                    width: 0.2em;
                    height: 0.25em;
                }

                .hamster__body {
                    animation: hamsterBody var(--dur) ease-in-out infinite;
                    background: hsl(270,90%,85%);
                    border-radius: 50% 30% 50% 30% / 15% 60% 40% 40%;
                    box-shadow: 0.1em 0.75em 0 hsl(270,90%,65%) inset,
                        0.15em -0.5em 0 hsl(270,90%,80%) inset;
                    top: 0.25em;
                    left: 2em;
                    width: 4.5em;
                    height: 3em;
                    transform-origin: 17% 50%;
                    transform-style: preserve-3d;
                }

                .hamster__limb--fr,
                .hamster__limb--fl {
                    clip-path: polygon(0 0, 100% 0, 70% 80%, 60% 100%, 0% 100%, 40% 80%);
                    top: 2em;
                    left: 0.5em;
                    width: 1em;
                    height: 1.5em;
                    transform-origin: 50% 0;
                }

                .hamster__limb--fr {
                    animation: hamsterFRLimb var(--dur) linear infinite;
                    background: linear-gradient(hsl(270,90%,80%) 80%, hsl(260,80%,70%) 80%);
                    transform: rotate(15deg) translateZ(-1px);
                }

                .hamster__limb--fl {
                    animation: hamsterFLLimb var(--dur) linear infinite;
                    background: linear-gradient(hsl(270,90%,85%) 80%, hsl(250,80%,75%) 80%);
                    transform: rotate(15deg);
                }

                .hamster__limb--br,
                .hamster__limb--bl {
                    border-radius: 0.75em 0.75em 0 0;
                    clip-path: polygon(0 0, 100% 0, 100% 30%, 70% 90%, 70% 100%, 30% 100%, 40% 90%, 0% 30%);
                    top: 1em;
                    left: 2.8em;
                    width: 1.5em;
                    height: 2.5em;
                    transform-origin: 50% 30%;
                }

                .hamster__limb--br {
                    animation: hamsterBRLimb var(--dur) linear infinite;
                    background: linear-gradient(hsl(270,90%,80%) 90%, hsl(260,80%,70%) 90%);
                    transform: rotate(-25deg) translateZ(-1px);
                }

                .hamster__limb--bl {
                    animation: hamsterBLLimb var(--dur) linear infinite;
                    background: linear-gradient(hsl(270,90%,85%) 90%, hsl(250,80%,75%) 90%);
                    transform: rotate(-25deg);
                }

                .hamster__tail {
                    animation: hamsterTail var(--dur) linear infinite;
                    background: hsl(250,80%,75%);
                    border-radius: 0.25em 50% 50% 0.25em;
                    box-shadow: 0 -0.2em 0 hsl(260,80%,70%) inset;
                    top: 1.5em;
                    right: -0.5em;
                    width: 1em;
                    height: 0.5em;
                    transform: rotate(30deg) translateZ(-1px);
                    transform-origin: 0.25em 0.25em;
                }

                .spoke {
                    animation: spoke var(--dur) linear infinite;
                    background: radial-gradient(100% 100% at center, hsl(270,80%,75%) 4.8%, hsla(270,80%,75%,0) 5%),
                        linear-gradient(hsla(270,80%,70%,0) 46.9%, hsl(270,80%,80%) 47% 52.9%, hsla(270,80%,80%,0) 53%) 50% 50% / 99% 99% no-repeat;
                }

                @keyframes hamster {
                    from, to {
                        transform: rotate(4deg) translate(-0.8em, 1.85em);
                    }
                    50% {
                        transform: rotate(0) translate(-0.8em, 1.85em);
                    }
                }

                @keyframes hamsterHead {
                    from, 25%, 50%, 75%, to {
                        transform: rotate(0);
                    }
                    12.5%, 37.5%, 62.5%, 87.5% {
                        transform: rotate(8deg);
                    }
                }

                @keyframes hamsterEye {
                    from, 90%, to {
                        transform: scaleY(1);
                    }
                    95% {
                        transform: scaleY(0);
                    }
                }

                @keyframes hamsterEar {
                    from, 25%, 50%, 75%, to {
                        transform: rotate(0);
                    }
                    12.5%, 37.5%, 62.5%, 87.5% {
                        transform: rotate(12deg);
                    }
                }

                @keyframes hamsterBody {
                    from, 25%, 50%, 75%, to {
                        transform: rotate(0);
                    }
                    12.5%, 37.5%, 62.5%, 87.5% {
                        transform: rotate(-2deg);
                    }
                }

                @keyframes hamsterFRLimb {
                    from, 25%, 50%, 75%, to {
                        transform: rotate(50deg) translateZ(-1px);
                    }
                    12.5%, 37.5%, 62.5%, 87.5% {
                        transform: rotate(-30deg) translateZ(-1px);
                    }
                }

                @keyframes hamsterFLLimb {
                    from, 25%, 50%, 75%, to {
                        transform: rotate(-30deg);
                    }
                    12.5%, 37.5%, 62.5%, 87.5% {
                        transform: rotate(50deg);
                    }
                }

                @keyframes hamsterBRLimb {
                    from, 25%, 50%, 75%, to {
                        transform: rotate(-60deg) translateZ(-1px);
                    }
                    12.5%, 37.5%, 62.5%, 87.5% {
                        transform: rotate(20deg) translateZ(-1px);
                    }
                }

                @keyframes hamsterBLLimb {
                    from, 25%, 50%, 75%, to {
                        transform: rotate(20deg);
                    }
                    12.5%, 37.5%, 62.5%, 87.5% {
                        transform: rotate(-60deg);
                    }
                }

                @keyframes hamsterTail {
                    from, 25%, 50%, 75%, to {
                        transform: rotate(30deg) translateZ(-1px);
                    }
                    12.5%, 37.5%, 62.5%, 87.5% {
                        transform: rotate(10deg) translateZ(-1px);
                    }
                }

                @keyframes spoke {
                    from {
                        transform: rotate(0);
                    }
                    to {
                        transform: rotate(-1turn);
                    }
                }
            `}</style>
        </div>
        );
    }

    // --- Render Provider ---
    return (
        <FirebaseContext.Provider value={contextValue}>
            {children}
        </FirebaseContext.Provider>
    );
}